/**
 * AudioManager — background music + game SFX, all synthesised via Web Audio API.
 *
 * Two independent output buses:
 *   musicBus  — gentle chord progression + melody.
 *               Silenced by the Music toggle.
 *   sfxBus    — drop ka-ching, settling thud, tower-fall crash.
 *               Silenced by the Effects toggle independently.
 *
 * Usage:
 *   import { musicManager } from './MusicManager';
 *   musicManager.ensureStarted();           // call inside a user-gesture handler
 *   musicManager.start();                   // alias used by HomeScene
 *   musicManager.setMuted(true/false);      // mutes music only
 *   musicManager.setSfxMuted(true/false);   // mutes effects only
 *   musicManager.isMuted()                  // returns bool
 *   musicManager.isSfxMuted()              // returns bool (also a plain property)
 *   musicManager.soundDrop();
 *   musicManager.soundSlide();
 *   musicManager.soundTowerFall();
 */

const CHORDS = [
  [261.63, 329.63, 392.00],
  [220.00, 261.63, 329.63],
  [174.61, 220.00, 261.63],
  [196.00, 246.94, 293.66],
];
const CHORD_DUR = 7;
const ATTACK    = 2.0;
const RELEASE   = 2.0;
const NOTE_VOL  = 0.06;
const LPF_FREQ  = 900;

class AudioManager {
  constructor() {
    this.ctx           = null;
    this.musicBus      = null;
    this.sfxBus        = null;
    this._started      = false;
    this._lastSlide    = 0;
    this._muted        = localStorage.getItem('foodstack_muted') === 'true';
    this._sfxMuted     = localStorage.getItem('foodstack_sfx_muted') === 'true';

    // expose as plain properties for parity with animalsstack API
    this.isMutedVal    = this._muted;
    this.isSfxMutedVal = this._sfxMuted;

    // MP3 drop sound buffer
    this.dropSoundBuffer = null;

    document.addEventListener('visibilitychange', () => {
      if (!this.ctx) return;
      if (document.hidden)    this.ctx.suspend();
      else if (this._started) this.ctx.resume();
    });
  }

  // ── Public API ────────────────────────────────────────────────

  /** isMuted as a method (keeps HomeScene/UIScene compatibility) */
  isMuted() { return this._muted; }

  /** isSfxMuted as both a method and a plain property */
  get isSfxMuted() { return this._sfxMuted; }

  ensureStarted() {
    if (!this._started) {
      this._started = true;
      this._init();
    } else if (this.ctx?.state === 'suspended') {
      this.ctx.resume();
    }
  }

  /** Alias used by HomeScene on play button press */
  start() { this.ensureStarted(); }

  /** Mute/unmute the music bus */
  setMuted(muted) {
    this._muted = muted;
    localStorage.setItem('foodstack_muted', muted ? 'true' : 'false');
    if (this.musicBus && this.ctx) {
      this.musicBus.gain.setTargetAtTime(muted ? 0 : 1.0, this.ctx.currentTime, 0.06);
    }
  }

  /** Toggle music mute — returns new muted state */
  toggleMute() {
    this.setMuted(!this._muted);
    return this._muted;
  }

  /** Mute/unmute the SFX bus */
  setSfxMuted(muted) {
    this._sfxMuted = muted;
    localStorage.setItem('foodstack_sfx_muted', muted ? 'true' : 'false');
    if (this.sfxBus && this.ctx) {
      this.sfxBus.gain.setTargetAtTime(muted ? 0 : 0.85, this.ctx.currentTime, 0.06);
    }
  }

  // ── Sound Loading ────────────────────────────────────────────

  async loadDropSound() {
    try {
      const response = await fetch('./assets/sounds/ka-ching.mp3');
      if (!response.ok) {
        console.warn('ka-ching.mp3 not found, using synthesized fallback');
        return;
      }
      const arrayBuffer = await response.arrayBuffer();
      this.dropSoundBuffer = await this.ctx.decodeAudioData(arrayBuffer);
      console.log('ka-ching.mp3 loaded successfully');
    } catch (e) {
      console.warn('Failed to load ka-ching sound, using synthesized fallback:', e);
    }
  }

  // ── SFX ──────────────────────────────────────────────────────

  /** Ka-ching cash register — plays MP3 if loaded, else synthesised */
  soundDrop() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;

    if (this.dropSoundBuffer) {
      const source   = ctx.createBufferSource();
      source.buffer  = this.dropSoundBuffer;
      const gainNode = ctx.createGain();
      gainNode.gain.value = 0.7;
      source.connect(gainNode);
      gainNode.connect(this.sfxBus);
      source.start();
      return;
    }

    // ── Fallback synthesised ka-ching ──────────────────────────
    const now = ctx.currentTime;

    const thudOsc = ctx.createOscillator();
    thudOsc.type = 'sine';
    thudOsc.frequency.setValueAtTime(180, now);
    thudOsc.frequency.exponentialRampToValueAtTime(60, now + 0.06);
    const thudGn = ctx.createGain();
    thudGn.gain.setValueAtTime(0.22, now);
    thudGn.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    thudOsc.connect(thudGn); thudGn.connect(this.sfxBus);
    thudOsc.start(now); thudOsc.stop(now + 0.07);

    const clickLen  = Math.floor(ctx.sampleRate * 0.018);
    const clickBuf  = ctx.createBuffer(1, clickLen, ctx.sampleRate);
    const clickData = clickBuf.getChannelData(0);
    for (let i = 0; i < clickLen; i++) clickData[i] = Math.random() * 2 - 1;
    const clickSrc = ctx.createBufferSource();
    clickSrc.buffer = clickBuf;
    const clickHp = ctx.createBiquadFilter();
    clickHp.type = 'highpass'; clickHp.frequency.value = 1800;
    const clickGn = ctx.createGain();
    clickGn.gain.setValueAtTime(0.14, now);
    clickGn.gain.exponentialRampToValueAtTime(0.001, now + 0.018);
    clickSrc.connect(clickHp); clickHp.connect(clickGn); clickGn.connect(this.sfxBus);
    clickSrc.start(now);

    const chingStart = now + 0.05;
    [2637, 3951, 5274].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type  = 'sine';
      osc.frequency.value = freq;
      const gn  = ctx.createGain();
      const vol = 0.13 - i * 0.03;
      gn.gain.setValueAtTime(vol, chingStart);
      gn.gain.exponentialRampToValueAtTime(0.001, chingStart + 0.55);
      osc.connect(gn); gn.connect(this.sfxBus);
      osc.start(chingStart); osc.stop(chingStart + 0.6);
    });

    const shimLen  = Math.floor(ctx.sampleRate * 0.55);
    const shimBuf  = ctx.createBuffer(1, shimLen, ctx.sampleRate);
    const shimData = shimBuf.getChannelData(0);
    for (let i = 0; i < shimLen; i++) shimData[i] = Math.random() * 2 - 1;
    const shimSrc = ctx.createBufferSource();
    shimSrc.buffer = shimBuf;
    const shimBp = ctx.createBiquadFilter();
    shimBp.type = 'bandpass'; shimBp.frequency.value = 4200; shimBp.Q.value = 4;
    const shimGn = ctx.createGain();
    shimGn.gain.setValueAtTime(0.07, chingStart);
    shimGn.gain.exponentialRampToValueAtTime(0.001, chingStart + 0.55);
    shimSrc.connect(shimBp); shimBp.connect(shimGn); shimGn.connect(this.sfxBus);
    shimSrc.start(chingStart);
  }

  /** Gentle settling thud — throttled to once per 550 ms */
  soundSlide() {
    const now = Date.now();
    if (now - this._lastSlide < 550) return;
    this._lastSlide = now;

    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type  = 'sine';
    osc.frequency.setValueAtTime(160, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.11);
    const gn = ctx.createGain();
    gn.gain.setValueAtTime(0, t);
    gn.gain.linearRampToValueAtTime(0.09, t + 0.02);
    gn.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
    osc.connect(gn); gn.connect(this.sfxBus);
    osc.start(t); osc.stop(t + 0.13);

    const bufLen = Math.floor(ctx.sampleRate * 0.09);
    const buf    = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const d      = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 420; bp.Q.value = 1.2;
    const rgn = ctx.createGain();
    rgn.gain.setValueAtTime(0.055, t);
    rgn.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
    src.connect(bp); bp.connect(rgn); rgn.connect(this.sfxBus);
    src.start(t);
  }

  /** Rumbling crash when the tower falls */
  soundTowerFall() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;

    [{ f: 90, t: 0.00, vol: 0.17 },
     { f: 58, t: 0.04, vol: 0.15 },
     { f: 72, t: 0.08, vol: 0.13 }].forEach(({ f, t, vol }) => {
      const osc = ctx.createOscillator();
      osc.type  = 'sine';
      osc.frequency.setValueAtTime(f, now + t);
      osc.frequency.exponentialRampToValueAtTime(f * 0.35, now + t + 0.75);
      const gn = ctx.createGain();
      gn.gain.setValueAtTime(0, now + t);
      gn.gain.linearRampToValueAtTime(vol, now + t + 0.035);
      gn.gain.exponentialRampToValueAtTime(0.001, now + t + 0.75);
      osc.connect(gn); gn.connect(this.sfxBus);
      osc.start(now + t); osc.stop(now + t + 0.8);
    });

    const bufLen = Math.floor(ctx.sampleRate * 0.45);
    const buf    = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const d      = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 700;
    const cgn = ctx.createGain();
    cgn.gain.setValueAtTime(0.16, now);
    cgn.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    src.connect(lp); lp.connect(cgn); cgn.connect(this.sfxBus);
    src.start(now);
  }

  // ── Backwards-compat alias ────────────────────────────────────
  /** Legacy name used in old GameScene — same as soundDrop */
  playCashRegister() { this.soundDrop(); }

  // ── Private ───────────────────────────────────────────────────

  _init() {
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();

    this.musicBus = this.ctx.createGain();
    this.musicBus.gain.value = this._muted ? 0 : 1.0;
    this.musicBus.connect(this.ctx.destination);

    this.sfxBus = this.ctx.createGain();
    this.sfxBus.gain.value = this._sfxMuted ? 0 : 0.85;
    this.sfxBus.connect(this.ctx.destination);

    this.loadDropSound().then(() => {
      this._scheduleChord(0, this.ctx.currentTime + 0.5);
    });
  }

  _scheduleChord(idx, startTime) {
    const freqs = CHORDS[idx % CHORDS.length];

    freqs.forEach(freq => {
      [-4, +4].forEach(detune => {
        const osc = this.ctx.createOscillator();
        osc.type  = 'triangle';
        osc.frequency.value = freq;
        osc.detune.value    = detune;

        const lpf = this.ctx.createBiquadFilter();
        lpf.type  = 'lowpass'; lpf.frequency.value = LPF_FREQ;

        const gn = this.ctx.createGain();
        gn.gain.setValueAtTime(0, startTime);
        gn.gain.linearRampToValueAtTime(NOTE_VOL * 0.5, startTime + ATTACK);
        gn.gain.setValueAtTime(NOTE_VOL * 0.5, startTime + CHORD_DUR - RELEASE);
        gn.gain.linearRampToValueAtTime(0, startTime + CHORD_DUR);

        osc.connect(lpf); lpf.connect(gn); gn.connect(this.musicBus);
        osc.start(startTime); osc.stop(startTime + CHORD_DUR + 0.1);
      });
    });

    const nextStart = startTime + CHORD_DUR - RELEASE;
    setTimeout(() => {
      if (this._started) this._scheduleChord(idx + 1, nextStart);
    }, Math.max(0, (nextStart - this.ctx.currentTime) * 1000 - 80));
  }
}

export const musicManager = new AudioManager();
export default musicManager;
